// src/Redux/Notifications/notificationsSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosWithAuth from '../UtilsRedux/axiosWithAuth';
import { API_ROUTES } from '../Constants/apiRoutes';
import { showFeedback } from '../UI/uiFeedbackSlice';

// List with cursor (pass {cursor, limit})
export const fetchNotifications = createAsyncThunk(
  'notifications/fetch',
  async ({ cursor = null, limit = 20 } = {}, thunkAPI) => {
     const { dispatch } = thunkAPI;
    try {
      const qs = new URLSearchParams();
      if (cursor) qs.set('cursor', cursor);
      if (limit) qs.set('limit', String(limit));
      const url = `${API_ROUTES.NOTIFICATIONS}${qs.toString() ? `?${qs}` : ''}`;
      const { data } = await axiosWithAuth.get(url);
      // expected: { success, items, nextCursor }
      return data;
    } catch (err) {
      const message =
        err?.response?.data?.message || 'Failed to fetch notifications';

      // only show popup for initial load, not for "load more"
      if (!cursor) {
        dispatch(
          showFeedback({
            type: 'error',
            title: 'Notifications error',
            message,
          })
        );
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Small fast endpoint for badge
export const fetchUnreadCount = createAsyncThunk(
  'notifications/unreadCount',
  async (_, thunkAPI) => {
    try {
      const { data } = await axiosWithAuth.get(API_ROUTES.NOTIFICATIONS_UNREAD_COUNT);
      return data.count || 0;
    } catch (err) {
      return thunkAPI.rejectWithValue(0);
    }
  }
);

// Mark one read
export const markNotificationRead = createAsyncThunk(
  'notifications/markRead',
  async (id, thunkAPI) => {
     const { dispatch } = thunkAPI;
    try {
      await axiosWithAuth.patch(API_ROUTES.NOTIFICATION_READ(id));
      return id;
    } catch (err) {
       // optional popup – I’m keeping this silent to avoid noise
      // const message =
      //   err?.response?.data?.message || 'Failed to update notification.';
      // thunkAPI.dispatch(
      //   showFeedback({
      //     type: 'error',
      //     title: 'Action failed',
      //     message,
      //   })
      // );
      return thunkAPI.rejectWithValue(id);
    }
  }
);

// Mark all read
export const markAllNotificationsRead = createAsyncThunk(
  'notifications/markAllRead',
  async (_, thunkAPI) => {
     const { dispatch } = thunkAPI;
    try {
      await axiosWithAuth.post(API_ROUTES.NOTIFICATIONS_MARK_ALL);
      return true;
    } catch (err) {
       const message =
        err?.response?.data?.message || 'Failed to mark notifications as read';

      dispatch(
        showFeedback({
          type: 'error',
          title: 'Action failed',
          message,
        })
      );

      return thunkAPI.rejectWithValue(false);
    }
  }
);

// Optional delete
export const deleteNotification = createAsyncThunk(
  'notifications/delete',
  async (id, thunkAPI) => {
     const { dispatch } = thunkAPI;
    try {
      await axiosWithAuth.delete(API_ROUTES.NOTIFICATION_DELETE(id));
      return id;
    } catch (err) {
      const message =
        err?.response?.data?.message || 'Failed to delete notification';

      dispatch(
        showFeedback({
          type: 'error',
          title: 'Delete failed',
          message,
        })
      );

      return thunkAPI.rejectWithValue(id);
    }
  }
);

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState: {
    items: [],
    nextCursor: null,
    status: 'idle',         // 'idle' | 'loading' | 'succeeded' | 'failed'
    loadingMore: false,
    unread: 0,
    error: null,
  },
  reducers: {
    // for future realtime (Socket/SSE)
    prependNotification(state, action) {
      const n = action.payload;
      if (!state.items.find(x => x._id === n._id)) {
        state.items.unshift(n);
        state.unread += n.readAt ? 0 : 1;
      }
    },
    resetNotifications(state) {
      state.items = [];
      state.nextCursor = null;
      state.status = 'idle';
      state.unread = 0;
    }
  },
  extraReducers: (b) => {
    b
      .addCase(fetchNotifications.pending, (s, a) => {
        const hasCursor = !!a.meta.arg?.cursor;
        s.status = hasCursor ? s.status : 'loading';
        s.loadingMore = hasCursor;
        s.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (s, a) => {
        const { items = [], nextCursor = null } = a.payload || {};
        if (a.meta.arg?.cursor) {
          // append older
          const existingIds = new Set(s.items.map(i => i._id));
          items.forEach(i => { if (!existingIds.has(i._id)) s.items.push(i); });
        } else {
          // first page
          s.items = items;
        }
        s.nextCursor = nextCursor;
        s.status = 'succeeded';
        s.loadingMore = false;
      })
      .addCase(fetchNotifications.rejected, (s, a) => {
        s.status = 'failed';
        s.loadingMore = false;
        s.error = a.payload || 'Fetch failed';
      })

      .addCase(fetchUnreadCount.fulfilled, (s, a) => {
        s.unread = typeof a.payload === 'number' ? a.payload : 0;
      })

      .addCase(markNotificationRead.fulfilled, (s, a) => {
        const id = a.payload;
        const n = s.items.find(x => x._id === id);
        if (n && !n.readAt) {
          n.readAt = new Date().toISOString();
          if (s.unread > 0) s.unread -= 1;
        }
      })

      .addCase(markAllNotificationsRead.fulfilled, (s) => {
        s.items.forEach(n => { if (!n.readAt) n.readAt = new Date().toISOString(); });
        s.unread = 0;
      })

      .addCase(deleteNotification.fulfilled, (s, a) => {
        const id = a.payload;
        const n = s.items.find(x => x._id === id);
        if (n && !n.readAt && s.unread > 0) s.unread -= 1;
        s.items = s.items.filter(x => x._id !== id);
      });
  }
});

export const { prependNotification, resetNotifications } = notificationsSlice.actions;
export default notificationsSlice.reducer;
